import { FieldDefinition, FieldType } from '../../models/field-definition';

/**
 * One captured dynamic-form field. The label and type travel next to the value so the data can be
 * rendered later without loading the form definition that produced it — form types are editable,
 * and a recording must keep showing the fields exactly as they were filled in.
 */
export interface FormDataEntry {
  key: string;
  label: string;
  type: FieldType;
  value: unknown;
  unique?: boolean;
  /** The field's case role (Judge, Defendant, ...), so saved recordings can rebuild their speakers. */
  referenceField?: string;
}

/** Snapshot of a filled dynamic form, in field order, ready to persist with the recording. */
export function buildFormDataSnapshot(
  fields: FieldDefinition[],
  values: Record<string, unknown>
): FormDataEntry[] {
  return fields.map(field => ({
    key: field.key,
    label: field.label,
    type: field.type,
    value: values[field.key] ?? null,
    unique: field.unique,
    referenceField: field.referenceField
  }));
}

export function parseFormDataSnapshot(json: string | null | undefined): FormDataEntry[] {
  if (!json) {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }

  if (Array.isArray(parsed)) {
    return parsed as FormDataEntry[];
  }

  // Recordings made before the snapshot carried labels hold a flat { key: value } bag.
  if (parsed && typeof parsed === 'object') {
    return Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({
      key,
      label: humanizeKey(key),
      type: FieldType.Text,
      value
    }));
  }

  return [];
}

/** Field keys are the label with its spaces stripped, so they are split back into words. */
function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
}
